import React from "react";
import { usePermissions } from "@/contexts/PermissionContext";
import { Permission } from "@/types/permissions";

interface PermissionGateProps {
  permission?: Permission;
  permissions?: Permission[];
  brandId?: number;
  branchId?: number;
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * PermissionGate component - conditionally renders children based on permissions
 *
 * @param permission - Single permission to check
 * @param permissions - Array of permissions to check
 * @param brandId - Brand ID to check access for
 * @param branchId - Branch ID to check access for
 * @param requireAll - If true, requires all permissions (default: false, requires any)
 * @param fallback - Component to render when access is denied
 * @param children - Content to render when access is granted
 */
export function PermissionGate({
  permission,
  permissions,
  brandId,
  branchId,
  requireAll = false,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { hasPermission, hasBrandAccess, hasBranchAccess } = usePermissions();

  // Check permission-based access
  let hasPermissionAccess = true;
  if (permission) {
    hasPermissionAccess = hasPermission(permission);
  } else if (permissions) {
    if (requireAll) {
      hasPermissionAccess = permissions.every((p) => hasPermission(p));
    } else {
      hasPermissionAccess = permissions.some((p) => hasPermission(p));
    }
  }

  // Check resource-based access
  let hasResourceAccess = true;
  if (brandId !== undefined) {
    hasResourceAccess = hasBrandAccess(brandId);
  }
  if (branchId !== undefined) {
    hasResourceAccess = hasResourceAccess && hasBranchAccess(branchId);
  }

  // Grant access only if both permission and resource access are granted
  const hasAccess = hasPermissionAccess && hasResourceAccess;

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

// Convenience components for common use cases
export function RequirePermission({
  permission,
  fallback = null,
  children,
}: {
  permission: Permission;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <PermissionGate permission={permission} fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function RequireBusinessAccess({
  brandId,
  fallback = null,
  children,
}: {
  brandId: number;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <PermissionGate brandId={brandId} fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function RequireBranchAccess({
  branchId,
  fallback = null,
  children,
}: {
  branchId: number;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <PermissionGate branchId={branchId} fallback={fallback}>
      {children}
    </PermissionGate>
  );
}
